<template>
  <div class="border-t border-gray-100 bg-gray-50">
    <!-- Category Status Grid -->
    <div v-if="categoryStatus && Object.keys(categoryStatus).length > 0" class="px-4 py-3">
      <h4 class="text-xs font-medium text-gray-700 mb-2">
        Category Status
      </h4>
      <div class="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <div
          v-for="(status, category) in categoryStatus"
          :key="category"
          class="flex items-center justify-between p-2 bg-white rounded border border-gray-100">
          <div class="flex items-center space-x-2">
            <div
              :class="[
                'w-2 h-2 rounded-full',
                isRecent(status.last_sync_at) ? 'bg-green-500' : 'bg-gray-300'
              ]" />
            <span class="text-xs text-gray-700 capitalize">{{ formatCategory(category) }}</span>
          </div>
          <span class="text-xs text-gray-400">
            {{ status.sample_count || 0 }}
          </span>
        </div>
      </div>
    </div>

    <!-- Recent Sync Events -->
    <div v-if="recentEvents && recentEvents.length > 0" class="px-4 py-3 border-t border-gray-100">
      <h4 class="text-xs font-medium text-gray-700 mb-2">
        Recent Activity
      </h4>
      <ul class="space-y-2">
        <li
          v-for="event in recentEvents.slice(0, 5)"
          :key="event.id"
          class="flex items-center justify-between text-xs">
          <div class="flex items-center space-x-2">
            <component :is="eventIcon(event.event_type)" class="w-4 h-4" :class="eventIconClass(event.event_type)" />
            <span class="text-gray-700">
              {{ eventDescription(event) }}
            </span>
          </div>
          <span class="text-gray-400">
            {{ formatTimeAgo(event.created_at) }}
          </span>
        </li>
      </ul>
    </div>

    <!-- Total Samples Count -->
    <div class="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
      <span class="text-xs text-gray-500">Total samples synced</span>
      <span class="text-sm font-medium text-gray-900">
        {{ formatNumber(totalSamples) }}
      </span>
    </div>
  </div>
</template>

<script>
import { h } from 'vue'

// Icon components
const CheckCircleIcon = {
  render() {
    return h('svg', { fill: 'none', viewBox: '0 0 24 24', stroke: 'currentColor' }, [
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
      })
    ])
  }
}

const XCircleIcon = {
  render() {
    return h('svg', { fill: 'none', viewBox: '0 0 24 24', stroke: 'currentColor' }, [
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z'
      })
    ])
  }
}

const ArrowDownIcon = {
  render() {
    return h('svg', { fill: 'none', viewBox: '0 0 24 24', stroke: 'currentColor' }, [
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M19 14l-7 7m0 0l-7-7m7 7V3'
      })
    ])
  }
}

export default {
  name: 'SyncHistoryPanel',

  props: {
    categoryStatus: {
      type: Object,
      default: () => ({})
    },
    recentEvents: {
      type: Array,
      default: () => []
    },
    totalSamples: {
      type: Number,
      default: 0
    }
  },

  methods: {
    formatCategory(category) {
      if (!category) return ''
      return category
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim()
    },

    formatNumber(num) {
      if (num === null || num === undefined) return '0'
      return num.toLocaleString()
    },

    formatTimeAgo(dateString) {
      if (!dateString) return ''

      const date = new Date(dateString)
      const now = new Date()
      const diffMs = now - date
      const diffSecs = Math.floor(diffMs / 1000)
      const diffMins = Math.floor(diffSecs / 60)
      const diffHours = Math.floor(diffMins / 60)
      const diffDays = Math.floor(diffHours / 24)

      if (diffSecs < 60) return 'just now'
      if (diffMins < 60) return `${diffMins}m`
      if (diffHours < 24) return `${diffHours}h`
      if (diffDays < 7) return `${diffDays}d`
      return date.toLocaleDateString()
    },

    isRecent(dateString) {
      if (!dateString) return false
      const date = new Date(dateString)
      const now = new Date()
      const diffHours = (now - date) / (1000 * 60 * 60)
      return diffHours < 24
    },

    eventIcon(eventType) {
      switch (eventType) {
        case 'sync_completed':
          return CheckCircleIcon
        case 'sync_failed':
          return XCircleIcon
        case 'batch_received':
        default:
          return ArrowDownIcon
      }
    },

    eventIconClass(eventType) {
      switch (eventType) {
        case 'sync_completed':
          return 'text-green-500'
        case 'sync_failed':
          return 'text-red-500'
        case 'batch_received':
        default:
          return 'text-blue-500'
      }
    },

    eventDescription(event) {
      switch (event.event_type) {
        case 'sync_completed':
          return `Sync completed (${event.samples_count || 0} samples)`
        case 'sync_failed':
          return 'Sync failed'
        case 'batch_received':
          return `Received ${event.samples_count || 0} samples`
        default:
          return event.event_type
      }
    }
  }
}
</script>
